import { NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  inject,
  Input,
  OnDestroy,
} from '@angular/core';
import { TrackingModule } from '@freelancer/tracking';
import { ModalService, UiModule } from '@freelancer/ui';
import { ModalColor, ModalSize } from '@freelancer/ui/modal';
import { PictureDisplay, PictureObjectFit } from '@freelancer/ui/picture';
import type { LaunchDeckCard } from './launch-deck.types';

@Component({
  standalone: true,
  imports: [NgStyle, TrackingModule, UiModule],
  selector: 'app-launch-deck-card',
  template: `
    <div
      class="Card"
      flTrackingLabel="LaunchDeckCard"
      [class.Card--comingSoon]="card.comingSoon"
      [class.Card--noInteraction]="modalMode"
      [class.Card--tiltActive]="isTiltActive"
      (click)="handleClick()"
      (pointerenter)="onPointerEnter($event)"
      (pointerdown)="onPointerDown($event)"
      (pointermove)="onPointerMove($event)"
      (pointerup)="onPointerUp($event)"
      (pointerleave)="onPointerLeave($event)"
      (pointercancel)="onPointerUp($event)"
    >
      <div
        class="Card-visual"
        [ngStyle]="{ transform: tiltTransform }"
      >
        <div class="Card-imageWrapper">
          <fl-picture
            class="CardImage"
            [alt]="card.title"
            [src]="card.imageSrc"
            [display]="PictureDisplay.BLOCK"
            [objectFit]="PictureObjectFit.COVER"
            [fullWidth]="true"
          ></fl-picture>
          @if (card.comingSoon) {
            <div class="Card-overlay">
              <span
                class="Card-overlayText font-bold uppercase text-light"
                i18n="Coming soon label"
                >Coming Soon</span
              >
            </div>
          }
        </div>
        @if (card.isNew) {
          <span
            class="Card-badge text-body-16"
            i18n="New card badge"
            >NEW</span
          >
        }
      </div>
    </div>
  `,
  styleUrls: ['./launch-deck-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LaunchDeckCardComponent implements OnDestroy {
  private modalService = inject(ModalService);
  private cdr = inject(ChangeDetectorRef);

  PictureDisplay = PictureDisplay;
  PictureObjectFit = PictureObjectFit;

  @Input() card: LaunchDeckCard;

  // Carousel mode: used for styling fixed width cards, disables 3D tilt to allow carousel scrolling.
  @HostBinding('attr.data-carousel')
  @Input()
  carouselMode = false;

  // Modal mode: removes max-width constraint for modal preview display.
  @HostBinding('attr.data-modal')
  @Input()
  modalMode = false;

  // 3D tilt transform style
  tiltTransform = '';

  private readonly MAX_TILT = 25; // Degrees

  // Cache the current card rect for performance - don't need to call getBoundingClientRect on every pointer event.
  private cardRect: DOMRect | null = null;
  private rafId: number | null = null;

  // Track if current card is being tilted. Tilt on hover/touch.
  isTiltActive = false;

  handleClick(): void {
    if (this.card.comingSoon || this.modalMode) {
      return;
    }
    this.openCardModal();
  }

  openCardModal(): void {
    this.modalService.open('LaunchDeckCardModal', {
      color: ModalColor.DARK,
      size: ModalSize.LARGE,
      mobileFullscreen: true,
      inputs: {
        card: this.card,
      },
    });
  }

  onPointerEnter(event: PointerEvent): void {
    if (this.card.comingSoon) {
      return;
    }

    // For mouse, activate tilt on enter (hover behavior)
    if (event.pointerType === 'mouse') {
      this.isTiltActive = true;
      const target = event.currentTarget as HTMLElement;
      this.cardRect = target.getBoundingClientRect();
    }
  }

  onPointerDown(event: PointerEvent): void {
    if (this.card.comingSoon) {
      return;
    }

    // For touch/pen, activate tilt on press and capture pointer
    // Skip in carousel mode to allow normal scrolling
    if (
      event.pointerType !== 'mouse' &&
      !this.carouselMode &&
      !this.isTiltActive
    ) {
      this.isTiltActive = true;
      const target = event.currentTarget as HTMLElement;
      target.setPointerCapture(event.pointerId);
      this.cardRect = target.getBoundingClientRect();
    }
  }

  onPointerMove(event: PointerEvent): void {
    // Skip if tilt not active or RAF already pending (throttle to ~60fps)
    if (!this.isTiltActive || this.rafId !== null) {
      return;
    }

    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;

      const rect = this.cardRect;
      if (!rect) {
        return;
      }

      // Calculate pointer position relative to card center (-0.5 to 0.5)
      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;

      // Calculate tilt angles (tilt towards pointer)
      const rotateX = y * this.MAX_TILT;
      const rotateY = x * -this.MAX_TILT;

      this.tiltTransform = `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.05)`;
      this.cdr.markForCheck();
    });
  }

  onPointerUp(event: PointerEvent): void {
    // For touch/pen, release capture and reset tilt
    if (event.pointerType !== 'mouse' && this.isTiltActive) {
      const target = event.currentTarget as HTMLElement;
      target.releasePointerCapture(event.pointerId);
      this.resetTilt();
    }
  }

  onPointerLeave(event: PointerEvent): void {
    if (event.pointerType === 'mouse') {
      this.resetTilt();
    }
  }

  private resetTilt(): void {
    this.cancelPendingRaf();
    this.cardRect = null;
    this.tiltTransform = '';
    this.isTiltActive = false;
    this.cdr.markForCheck();
  }

  private cancelPendingRaf(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  ngOnDestroy(): void {
    this.cancelPendingRaf();
  }
}
